import React from "react"
import * as d3 from "d3"
import * as _ from "lodash"
import {categories, type_counts, CATEGORY_NAMES, H3_LEVEL} from "./data.js"
import {titleCase} from "./utils.js"

export const moduleName = "311"

export const sectionOrder = [
  "mapOfBoston",
  "introHex",
  "issueDensityMap",
  "mostCommonIssues",
  "timeliness",
  "conclusions"
]

let divergingLogScale = d3
  .scaleDivergingLog(
    // Modulate the fixed scale for the given hex size to a range that display reasonable variation
    [0.75, 1, 1.5],
    d3.interpolatePiYG
  )
  .base(2)
  .clamp(true)

const groupedByCategory = _.groupBy(categories, d => d.category)
const categoryTypeSubtitle = _.fromPairs(
  CATEGORY_NAMES.map(d => [
    d,
    _.sortBy(groupedByCategory[d].map(d => d.type), t => -type_counts[t])
      .slice(0, 7)
      .join(" • ")
  ])
)

export const sections = {
  mapOfBoston: {
    buttonTitle: "A Map of Boston",
    body: () => (
      <div>
        <h1>A Map of Boston</h1>
        <div className="App-subtitle">Our city and its places</div>
        <p>
          Before looking at a map of the 311 service requests and learning more about
          local issues in our city, let's orient ourselves by looking at a map of
          Boston.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "issues_normalized_by_population"
      let format = d3.format("d")
      return {
        property,
        color: Object.assign(
          d => "transparent",
          d3.scaleSequential(
            d3.extent(features, d => d.properties[property]),
            d3.interpolateRdPu
          )
        ),
        opacity: 0,
        legendFormat: d => format(100 * d)
      }
    }
  },

  introHex: {
    buttonTitle: "Intro to the Hex Grid",
    body: () => (
      <div>
        <h1>Intro to 311 Data</h1>
        <div className="App-subtitle">From points to hexagons</div>

        <p>
          The data we’re exploring is 311 service requests. Each request is tagged
          with a point on the map.
        </p>
        <p>
          There are over 200,000 such requests just in the past two years, however,
          and it’s hard to see patterns at such a granular scale.
        </p>
        <p>
          To make the data patterns easier to see, we can bucket the data points into
          a hexagonal grid. Each hexagon is about the same size as an urban
          block, containing all the local requests within it.
        </p>
        <p>
          The hexagons set up a framework that helps us see the perceptions of the
          citizens that live, work, and travel within our city’s blocks and streets.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "issues_normalized_by_population"
      return {
        property,
        color: Object.assign(
          d => "transparent",
          d3.scaleSequential(
            d3.extent(features, d => d.properties[property]),
            d3.interpolateRdPu
          )
        ),
        stroke: true,
        opacity: 0,
        legendFormat: d3.format("d")
      }
    }
  },

  issueDensityMap: {
    buttonTitle: "Issue Density Map",
    hoverInfo: ({hoverFeatures, sectionState}) => {
      let round = d3.format(".2f")
      // note: Averaging the smoothed means is a very strange operation, since the amount of data
      // that went into each hex estimate may be very different. A better way to do this is to go
      // back to the source data and perform averaging on the aggregated raw values. But this is
      // close enough for now.
      let mean = d3.mean(hoverFeatures.map(d => d.properties[sectionState.property]))
      let sum = d3.sum(hoverFeatures.map(d => d.properties[sectionState.property]))
      if (mean === undefined) {
        return <div />
      }
      let format = d3.format(",.0f")
      let showingIssues = sectionState.property === "issues_normalized_by_population"

      return (
        <div className="hover-info">
          {showingIssues && (
            <div>
              <b>{format(100 * mean)} issues</b> per 100 people here.
            </div>
          )}
          {!showingIssues && (
            <div>
              <b>{format(sum)}</b> people live here.
            </div>
          )}
        </div>
      )
    },
    body: ({features, sectionState, setSectionState, hexes, property, color}) => {
      let showingIssues = sectionState.property === "issues_normalized_by_population"
      let format = d3.format("d")

      let linkClick = () => {
        let property = showingIssues
          ? "hex_population"
          : "issues_normalized_by_population"
        let domain = showingIssues ? d3.extent(hexes.features, d => d.properties[property]) : [0, 2]
        setSectionState(
          Object.assign({}, sectionState, {
            property,
            color: d3.scaleSequential(domain, d3.interpolateRdPu),
          })
        )
      }

      return (
        <div>
          <h1>Issue Density Map</h1>
          <div className="App-subtitle">Where are the issues happening?</div>

          <p>
            Here is the same hex grid, colored by the number of 311 issues per person
            in that bin.
          </p>

          <p>
            We can see that even normalized for population, the highest issue density
            reporting is in the most populous areas like the North End and South
            Boston. What might this tell us about the relationship between aspects of
            urban life such as population density, heavy pedestrian activity, local
            income levels, and the condition of our shared civic spaces?{" "}
          </p>

          <p>
            Right now, the map is
            {showingIssues
              ? [" showing issues per person. ", <span key='span' onClick={linkClick} className="link">See population density instead?</span>]
              : [" showing population. ", <span key='span' onClick={linkClick} className="link">See issues per person instead?</span>]}
          </p>
        </div>
      )
    },
    defaultState: features => {
      let property = "issues_normalized_by_population"
      let format = d3.format(",.0d")
      return {
        property,
        color: d3.scaleSequential(
          // clip off at the high end; the data currently goes to 6 but this way
          // we keep a linear scale while seeing variation in the range we care about
          [0, 2],
          d3.interpolateRdPu
        ),
        opacity: 1,
        legendFormat: d => format(100 * d),
      }
    }
  },

  mostCommonIssues: {
    buttonTitle: "Most Common Issues",
    hoverInfo: ({hoverFeatures, sectionState}) => {
      let mean = d3.mean(hoverFeatures.map(d => d.properties[sectionState.property]))
      if (mean === undefined) {
        return <div />
      }
      let round = d3.format(".2f")
      let percent = d3.format(".0%")
      let pc = percent(Math.abs(mean - 1))

      return (
        <div className="hover-info">
          <div>
            <b className={mean >= 1 ? "more" : "less"}>
              {pc.length === 2 ? <span style={{opacity: 0.001}}>0</span> : ""}
              {pc} {mean >= 1 ? "more than average" : "less than average"}
            </b>{" "}
            issues related to {sectionState.category.toLowerCase()}.
          </div>
        </div>
      )
    },
    body: ({sectionState, setSectionState}) => {
      return (
        <div>
          <h1>Most Common Issues</h1>
          <div className="App-subtitle">
            What are the most common issues in Boston’s local places?
          </div>
          <p>
            Here is the same hex grid, colored by issue categories. Areas with green
            hexagons show higher than average reportings of the issue while purple
            areas report the issue less than average.
          </p>{" "}
          {CATEGORY_NAMES.map(c => (
            <div
              key={c}
              className={
                "sidebar-option" + (c === sectionState.category ? " active" : "")
              }
              onClick={() =>
                setSectionState(
                  Object.assign({}, sectionState, {
                    category: c,
                    property: `category_ratio_${c}`,
                    color: divergingLogScale
                  })
                )
              }>
              <div className="title">{titleCase(c)}</div>
              {sectionState.category === c && (
                <div className="sample">{categoryTypeSubtitle[c]}</div>
              )}
            </div>
          ))}
        </div>
      )
    },
    defaultState: features => ({
      category: CATEGORY_NAMES[0],
      property: `category_ratio_${CATEGORY_NAMES[0]}`,
      color: divergingLogScale,
      opacity: 1,
      legendFormat: d3.format(".0%")
    })
  },

  timeliness: {
    buttonTitle: "Timeliness",
    hoverInfo: ({hoverFeatures, sectionState}) => {
      let mean = d3.mean(hoverFeatures.map(d => d.properties[sectionState.property]))
      if (mean === undefined) {
        return <div />
      }
      let round = d3.format(".2f")
      let percent = d3.format(".0%")
      let pc = percent(mean)

      return (
        <div className="hover-info">
          <div>
            <b>{pc} of issues</b> are addressed on time here.
          </div>
        </div>
      )
    },
    body: () => (
      <div>
        <h1>Timeliness</h1>
        <div className="App-subtitle">How long until issues are resolved?</div>

        <p>
          The city government has processes to handle the wide range of issues filed.
          A system is in place for notifying the appropriate city department to deal
          with the reported issue when a 311 report is created.
        </p>

        <p>
          Some, like shoveling snow, are time-sensitive and relatively low-effort.
          Others, such as tree removal, need to be scheduled in advance. Some deal
          with private or federal property and can’t be dealt with at all by the City
          of Boston.
        </p>

        <p>
          The map to the right shows the average rate of response for issues of trash pickup, parking, and abandoned vehicles across parts of the city.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "on_time_pc_smoothed"
      return {
        property,
        color: d3.scaleSequential(
          d3.extent(features, d => d.properties[property]),
          d3.interpolateRdPu
        ),
        opacity: 1,
        legendFormat: d3.format('.0%'),
      }
    }
  },

  conclusions: {
    buttonTitle: "Conclusions",
    body: () => (
      <div>
        <h1>Conclusions</h1>
        <div className="App-subtitle">
          What can data tell about the challenges and conditions of our city?
        </div>

        <p>
          Does the 311 data set support your own experience of Boston’s places? What
          would the map look like if all Bostonians reported their concerns? Can data
          like this help us to better address issues of social equity or inspire
          local advocacy?
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "issues_normalized_by_population"
      return {
        property,
        color: Object.assign(
          d => "transparent",
          d3.scaleSequential(
            d3.extent(features, d => d.properties[property]),
            d3.interpolateRdPu
          )
        ),
        opacity: 0,
        legendFormat: d3.format("d"),
        stroke: true,
      }
    }
  }
}

export const appIntro = (
  <>
    <h1>Exploring Boston’s Issues, Block-by-Block</h1>
    <div className="App-subtitle">What do Bostonians see and report?</div>
    <div className="App-intro-ps">
    <p>Knowledge and communication form the supporting foundation for any successful intervention in city design. Boston 311 exists to connect citizens to the city. </p>

    <p>Data such as public 311 call logs are one source of such knowledge about the city and its challengers—exploring and analyzing relevant information about local conditions can create insights,suggest solutions, and tell us more about how Boston’s citizens see their local places.  </p>

    <p>What does the 311 data has to say about your neighborhood?</p>

    </div>
  </>
)
